import { useState, useEffect } from "react";
import { CheckCircle, Circle, Clock, BookOpen, ChevronDown, ChevronRight, Lock, Loader2, Sparkles, Info, Play } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/hooks/useCredits";
import { hasToolAccess, type PlanTier } from "@/config/pricing";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { courseData, TOOL_KEY_MAP, type CourseLesson } from "@/data/courseContent";

function UpgradeBanner({ toolName }: { toolName: string }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 mb-4">
      <Info className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
      <p className="text-xs text-muted-foreground leading-relaxed">
        This lesson features <span className="font-medium text-foreground">{toolName}</span>, which is available on the Creator and Pro plans.
        You can read the full lesson now — when you're ready to use the tool hands-on, you can explore what the{" "}
        <Link to="/pricing" className="text-primary hover:underline font-medium">Creator plan</Link> includes.
      </p>
    </div>
  );
}

/** Check if a lesson requires a tool the user doesn't have access to */
function getLessonUpgradeTool(lesson: CourseLesson, userTier: PlanTier | "lifetime" | null): string | null {
  if (!lesson.requiredToolKey) return null;
  if (hasToolAccess(userTier, lesson.requiredToolKey)) return null;
  // Find the display name for this tool
  const displayName = Object.entries(TOOL_KEY_MAP).find(([, key]) => key === lesson.requiredToolKey)?.[0];
  return displayName || lesson.toolFeatured;
}

export default function StoryCraftFormula() {
  const { user, subscription } = useAuth();
  const { planTier, isLoading: creditsLoading } = useCredits();
  const userTier = (subscription.isLifetime ? "lifetime" : (planTier || subscription.planTier || "free")) as PlanTier | "lifetime";

  const [completedLessons, setCompletedLessons] = useState<Set<string>>(new Set());
  const [selectedLesson, setSelectedLesson] = useState<CourseLesson | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const [openModules, setOpenModules] = useState<Set<number>>(new Set([1]));

  const allLessons = courseData.modules.flatMap(m => m.lessons);
  const totalLessons = allLessons.length;
  const completedCount = completedLessons.size;
  const progressPercent = totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0;

  useEffect(() => {
    const loadProgress = async () => {
      if (!user) {
        setIsLoading(false);
        setSelectedLesson(allLessons[0] || null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('lesson_progress')
          .select('lesson_id, completed')
          .eq('user_id', user.id);

        if (error) throw error;

        const completed = new Set(
          (data || []).filter(p => p.completed).map(p => p.lesson_id)
        );
        setCompletedLessons(completed);

        const firstIncomplete = allLessons.find(l => !completed.has(l.lessonNumber));
        setSelectedLesson(firstIncomplete || allLessons[0] || null);

        if (firstIncomplete) {
          const moduleNum = parseInt(firstIncomplete.lessonNumber.split('.')[0]);
          setOpenModules(new Set([moduleNum]));
        }
      } catch (error) {
        console.error('Error loading progress:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProgress();
  }, [user]);

  const toggleCompletion = async (lessonNumber: string) => {
    if (!user) {
      toast.error('Please log in to track progress');
      return;
    }

    setIsUpdating(lessonNumber);
    const newCompleted = !completedLessons.has(lessonNumber);

    try {
      const { error } = await supabase
        .from('lesson_progress')
        .upsert({
          user_id: user.id,
          lesson_id: lessonNumber,
          completed: newCompleted,
          completed_at: newCompleted ? new Date().toISOString() : null
        }, {
          onConflict: 'user_id,lesson_id'
        });

      if (error) throw error;

      setCompletedLessons(prev => {
        const next = new Set(prev);
        if (newCompleted) next.add(lessonNumber);
        else next.delete(lessonNumber);
        return next;
      });

      toast.success(newCompleted ? 'Lesson marked as complete!' : 'Lesson marked as incomplete');
    } catch (error) {
      console.error('Error updating progress:', error);
      toast.error('Failed to update progress');
    } finally {
      setIsUpdating(null);
    }
  };

  const toggleModule = (moduleNumber: number) => {
    setOpenModules(prev => {
      const next = new Set(prev);
      if (next.has(moduleNumber)) next.delete(moduleNumber);
      else next.add(moduleNumber);
      return next;
    });
  };

  const selectLesson = (lesson: CourseLesson) => {
    setSelectedLesson(lesson);
  };

  if (isLoading) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const upgradeTool = selectedLesson ? getLessonUpgradeTool(selectedLesson, userTier) : null;

  return (
    <div className="p-6 md:p-8 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="font-display text-xl font-bold text-foreground">{courseData.title}</h1>
            <p className="text-sm text-muted-foreground">{courseData.subtitle}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 mt-6">
          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-4">
            {/* Progress */}
            <div className="bg-card/50 border border-border/50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-foreground">Course Progress</span>
                <span className="text-sm text-muted-foreground">{completedCount}/{totalLessons} lessons</span>
              </div>
              <Progress value={progressPercent} className="h-1.5 mb-2" />
              <p className="text-xs text-muted-foreground">
                {progressPercent}% complete{progressPercent === 100 ? " — Congratulations! 🎉" : ""}
              </p>
            </div>

            {/* Module List */}
            <div className="space-y-2">
              {courseData.modules.map((module) => {
                const moduleLessonCount = module.lessons.length;
                const moduleCompleted = module.lessons.filter(l => completedLessons.has(l.lessonNumber)).length;
                const isOpen = openModules.has(module.moduleNumber);

                return (
                  <Collapsible key={module.moduleNumber} open={isOpen} onOpenChange={() => toggleModule(module.moduleNumber)}>
                    <div className="bg-card/50 border border-border/50 rounded-xl overflow-hidden">
                      <CollapsibleTrigger className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/30 transition-colors">
                        {isOpen ? (
                          <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        )}
                        <div className="flex-1 text-left">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-primary">MODULE {module.moduleNumber}</span>
                          </div>
                          <p className="text-sm font-medium text-foreground mt-0.5 leading-tight">{module.title}</p>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {moduleCompleted}/{moduleLessonCount}
                        </span>
                      </CollapsibleTrigger>

                      <CollapsibleContent>
                        <div className="px-2 pb-2 space-y-0.5">
                          {module.lessons.map((lesson) => {
                            const isActive = selectedLesson?.lessonNumber === lesson.lessonNumber;
                            const isCompleted = completedLessons.has(lesson.lessonNumber);

                            return (
                              <div
                                key={lesson.lessonNumber}
                                onClick={() => selectLesson(lesson)}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all ${
                                  isActive
                                    ? "bg-primary/15 border-l-2 border-primary"
                                    : "hover:bg-muted/30"
                                }`}
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCompletion(lesson.lessonNumber);
                                  }}
                                  disabled={isUpdating === lesson.lessonNumber}
                                  className="flex-shrink-0 transition-transform hover:scale-110 disabled:opacity-50"
                                >
                                  {isUpdating === lesson.lessonNumber ? (
                                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                                  ) : isCompleted ? (
                                    <CheckCircle className="w-4 h-4 text-primary" />
                                  ) : (
                                    <Circle className="w-4 h-4 text-muted-foreground/50" />
                                  )}
                                </button>

                                <div className="flex-1 min-w-0">
                                  <span className={`text-sm block truncate ${isActive ? "font-medium text-foreground" : "text-muted-foreground"}`}>
                                    <span className="text-xs text-muted-foreground mr-1.5">{lesson.lessonNumber}</span>
                                    {lesson.title}
                                  </span>
                                </div>

                                <span className="text-xs text-muted-foreground whitespace-nowrap">{lesson.duration}</span>
                              </div>
                            );
                          })}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-8 space-y-4">
            {selectedLesson ? (
              <div className="bg-card/50 border border-border/50 rounded-xl p-5 md:p-6">
                {/* Lesson Header */}
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div>
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className="text-xs font-semibold text-primary">LESSON {selectedLesson.lessonNumber}</span>
                      <Badge variant="secondary" className="text-xs">
                        <Sparkles className="w-3 h-3 mr-1" />
                        {selectedLesson.toolFeatured}
                      </Badge>
                    </div>
                    <h2 className="font-display text-lg font-semibold text-foreground">
                      {selectedLesson.title}
                    </h2>
                    <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
                      <Clock className="w-3.5 h-3.5" />
                      {selectedLesson.duration}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleCompletion(selectedLesson.lessonNumber)}
                    disabled={isUpdating === selectedLesson.lessonNumber}
                    className={`shrink-0 ${completedLessons.has(selectedLesson.lessonNumber) ? "border-primary/50 text-primary" : ""}`}
                  >
                    {isUpdating === selectedLesson.lessonNumber ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : completedLessons.has(selectedLesson.lessonNumber) ? (
                      <CheckCircle className="w-4 h-4 mr-2 text-primary" />
                    ) : (
                      <Circle className="w-4 h-4 mr-2" />
                    )}
                    {completedLessons.has(selectedLesson.lessonNumber) ? "Completed" : "Mark Complete"}
                  </Button>
                </div>

                {/* Upgrade Banner */}
                {upgradeTool && <UpgradeBanner toolName={upgradeTool} />}

                {/* Video Player */}
                <div className="rounded-lg overflow-hidden border border-border/50 mb-5">
                  <AspectRatio ratio={16 / 9}>
                    {selectedLesson.driveFileId ? (
                      <iframe
                        src={`https://drive.google.com/file/d/${selectedLesson.driveFileId}/preview`}
                        className="w-full h-full"
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                        title={selectedLesson.title}
                      />
                    ) : (
                      <div className="w-full h-full bg-muted/50 flex flex-col items-center justify-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center">
                          <Play className="w-6 h-6 text-primary ml-0.5" />
                        </div>
                        <p className="text-sm text-muted-foreground">Video coming soon</p>
                      </div>
                    )}
                  </AspectRatio>
                </div>

                {/* Lesson Summary */}
                <div className="mb-5">
                  <h3 className="text-sm font-semibold text-foreground mb-2">What You'll Learn</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{selectedLesson.summary}</p>
                </div>

                {/* Homework */}
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-3">
                  <h3 className="text-sm font-semibold text-foreground mb-1 flex items-center gap-2">
                    📝 Homework Assignment
                  </h3>
                  <p className="text-xs text-muted-foreground">Complete this before moving to the next lesson.</p>
                </div>
                <div className="prose prose-sm dark:prose-invert max-w-none text-muted-foreground leading-relaxed whitespace-pre-line">
                  {selectedLesson.homeworkContent}
                </div>
              </div>
            ) : (
              <div className="bg-card/50 border border-border/50 rounded-xl p-8 text-center">
                <BookOpen className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">Select a lesson to get started</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
